/**
 * sheet_controller.js — presents every Bootstrap modal as a bottom sheet on touch.
 *
 * Mounted ONCE on <body> (data-controller="sheet" in templates/base.html). It
 * does not need to be attached to each modal, and deliberately is not: Bootstrap
 * dispatches show.bs.modal / hidden.bs.modal on the modal element and they
 * bubble, so listening at the body covers all ~40 modal templates in the app
 * plus every one added later, with zero template edits.
 *
 * Division of labour, and it is worth keeping straight:
 *   - BOOTSTRAP owns modal MECHANICS: show/hide, the backdrop, the focus trap,
 *     body scroll lock, stacking and aria. None of that is reimplemented here,
 *     because reimplementing it is how a modal system loses its accessibility.
 *   - THIS CONTROLLER owns modal PRESENTATION: which modals become sheets, the
 *     grab handle, the drag-to-dismiss, and the <turbo-frame id="modal">
 *     lifecycle.
 *   - static/css/components/sheet.css owns the LOOK, gated on the .is-sheet
 *     class this controller adds.
 *   - static/js/gestures.js owns the drag PHYSICS.
 *
 * The turbo-frame handling below used to be an inline <script> at the foot of
 * templates/base.html. Inline script in a template breaks after a Turbo
 * navigation, is invisible to anyone grepping for modal wiring, and violates the
 * project's frontend separation rule. It lives here now.
 *
 * On a native shell a modal that declares a sheet URL is handed to the native
 * sheet host instead (see bridge/sheet_controller.js); if no host takes it, the
 * web drawer below runs. That check is in show(), and how a modal declares its
 * URL is documented on nativeUrlFor() below.
 */

import { Controller } from "@hotwired/stimulus";
import { bump, crossedThreshold, lockAxis, motionToken, rubberBand } from "../../gestures.js";

// Chrome on Android can report (hover: none) as false while still being a
// touchscreen, so the pointer test carries the query. Both halves are needed.
const TOUCH_QUERY = "(hover: none) or (pointer: coarse)";

const DISMISS_MIN_PX = 90;      // absolute floor before a downward drag dismisses
const DISMISS_FRACTION = 0.25;  // ...or this fraction of the sheet's height
const AXIS_DEADZONE = 6;        // px before committing to an axis
const RESIST = 160;             // upward drag has nowhere to go; damp it hard
const RESIST_SCALE = 0.3;

// Placeholder Jinja renders into data-native-url-template via
// url_for(endpoint, id="__ID__"). Deliberately not a bare "0" or ":id" — the
// substitution is a plain string replace, so the token has to be something that
// cannot occur anywhere else in a Chippie URL.
const ID_TOKEN = "__ID__";

/**
 * The URL a native sheet should load for this modal, or null for "no native
 * presentation — paint the web drawer".
 *
 * Two ways a modal declares one, because the app opens modals two ways and both
 * have to keep url_for() as the owner of route shapes. Building a path in JS
 * here would put the route pattern in a second place, which is the exact bug
 * this epic exists to remove.
 *
 *   data-native-url
 *       The whole URL, already rendered. Used where the server knows the entity
 *       at render time — the <turbo-frame id="modal"> modals, which are fetched
 *       per row (components/modals/edit_*_frame.html).
 *
 *   data-native-url-template + data-native-url-id-field
 *       A url_for() with ID_TOKEN standing in for the entity id, plus the DOM id
 *       of the hidden input that JS fills in before showing. Used by the
 *       always-present modals that one page reuses for every row, where the
 *       entity is not known until open time.
 *
 * An unpopulated id field means the modal is being shown for something other
 * than an entity edit, so it returns null and the web drawer runs. Failing to
 * the drawer is always safe; guessing a URL is not.
 */
function nativeUrlFor(modal) {
    if (modal.dataset.nativeUrl) return modal.dataset.nativeUrl;

    const template = modal.dataset.nativeUrlTemplate;
    const fieldId = modal.dataset.nativeUrlIdField;
    if (!template || !fieldId) return null;

    // Scoped to the modal, not document.getElementById: fieldId names a field
    // INSIDE this modal, and a document-wide lookup would happily match a stale
    // turbo-frame mid-swap or a second modal that reuses the id, opening the
    // sheet on the wrong record. Attribute selector rather than "#" + id so an
    // id needing escaping cannot throw here.
    const field = modal.querySelector('[id="' + fieldId.replace(/"/g, '\\"') + '"]');
    const id = field && field.value;
    if (!id) return null;
    return template.replace(ID_TOKEN, encodeURIComponent(id));
}

export default class extends Controller {
    connect() {
        this.touch = window.matchMedia(TOUCH_QUERY);
        this.drag = null;

        this._onLinkClick = this.linkClick.bind(this);
        this._onShow = this.show.bind(this);
        this._onHidden = this.hidden.bind(this);
        this._onFrameLoad = this.frameLoad.bind(this);
        this._onSubmitEnd = this.submitEnd.bind(this);
        this._onDown = this.down.bind(this);
        this._onMove = this.move.bind(this);
        this._onUp = this.up.bind(this);

        this.element.addEventListener("click", this._onLinkClick);
        this.element.addEventListener("show.bs.modal", this._onShow);
        this.element.addEventListener("hidden.bs.modal", this._onHidden);
        document.addEventListener("turbo:frame-load", this._onFrameLoad);
        document.addEventListener("turbo:submit-end", this._onSubmitEnd);
        this.element.addEventListener("pointerdown", this._onDown);
        window.addEventListener("pointermove", this._onMove, { passive: false });
        window.addEventListener("pointerup", this._onUp);
        window.addEventListener("pointercancel", this._onUp);
    }

    disconnect() {
        this.element.removeEventListener("click", this._onLinkClick);
        this.element.removeEventListener("show.bs.modal", this._onShow);
        this.element.removeEventListener("hidden.bs.modal", this._onHidden);
        document.removeEventListener("turbo:frame-load", this._onFrameLoad);
        document.removeEventListener("turbo:submit-end", this._onSubmitEnd);
        this.element.removeEventListener("pointerdown", this._onDown);
        window.removeEventListener("pointermove", this._onMove);
        window.removeEventListener("pointerup", this._onUp);
        window.removeEventListener("pointercancel", this._onUp);
        this.drag = null;
    }

    // MARK: presentation

    /**
     * Offer a modal-opening link to the native sheet BEFORE Turbo fetches the
     * <turbo-frame id="modal"> it points at.
     *
     * Without this a tap costs two round trips: Turbo fetches the web modal, the
     * modal shows, show.bs.modal fires, and only THEN is the bridge asked — which
     * throws away the modal it just fetched and loads the sheet URL instead.
     * Measured in nginx's access log as a GET of the edit-modal frame immediately
     * followed by a GET of the standalone form page, on every single tap.
     *
     * The link carries the sheet URL as data-native-url, rendered by url_for in
     * the template, so route shapes still have exactly one owner — the same
     * contract nativeUrlFor() keeps for the modal element.
     *
     * Falls through untouched when nothing native answers, which is the browser
     * and PWA path: Turbo then navigates the frame exactly as it always did.
     */
    linkClick(event) {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const link = event.target.closest && event.target.closest("a[data-native-url]");
        if (!link) return;

        const bridge = window.ChippieBridge;
        if (!bridge || typeof bridge.presentSheet !== "function") return;

        let taken = false;
        try {
            taken = bridge.presentSheet(link.dataset.nativeUrl);
        } catch (_e) {
            taken = false;
        }
        // Only swallow the click when a native host actually took the job; a
        // false positive here would leave the tap doing nothing at all.
        if (taken) event.preventDefault();
    }

    show(event) {
        const modal = event.target;
        if (!modal.classList || !modal.classList.contains("modal")) return;

        // A native host, when one exists, presents this URL itself in a real
        // platform sheet. Returning truthy means it took the job, so the web
        // drawer must not also paint. Absent facade or no host -> falsy -> the
        // drawer runs, which is the browser and PWA path.
        //
        const nativeUrl = nativeUrlFor(modal);
        if (nativeUrl && window.ChippieBridge && window.ChippieBridge.presentSheet) {
            let taken = false;
            try {
                taken = window.ChippieBridge.presentSheet(nativeUrl);
            } catch (_e) {
                taken = false;
            }
            if (taken) {
                event.preventDefault();
                return;
            }
        }

        if (!this.touch.matches) return;
        modal.classList.add("is-sheet");
        this.addHandle(modal);
    }

    hidden(event) {
        const modal = event.target;
        if (!modal.classList || !modal.classList.contains("modal")) return;
        modal.classList.remove("is-sheet");
        this.resetDialog(modal);

        // A modal delivered through <turbo-frame id="modal"> is transient markup.
        // Emptying the frame on close means the next request re-fetches it rather
        // than reviving a stale instance Bootstrap has already torn down.
        const frame = modal.closest("turbo-frame#modal");
        if (frame) frame.innerHTML = "";
    }

    addHandle(modal) {
        const content = modal.querySelector(".modal-content");
        if (!content || content.querySelector(".sheet-handle")) return;
        const handle = document.createElement("div");
        handle.className = "sheet-handle";
        // The grab bar is decoration; the accessible way to dismiss is the
        // modal's own close button and Escape, both of which Bootstrap owns.
        handle.setAttribute("aria-hidden", "true");
        handle.innerHTML = '<span class="sheet-handle-bar"></span>';
        content.prepend(handle);
    }

    // MARK: drag to dismiss

    down(event) {
        const handle = event.target.closest && event.target.closest(".sheet-handle");
        if (!handle) return;
        const modal = handle.closest(".modal.is-sheet");
        if (!modal) return;
        const dialog = modal.querySelector(".modal-dialog");
        if (!dialog) return;

        this.drag = {
            modal,
            dialog,
            startX: event.clientX,
            startY: event.clientY,
            dy: 0,
            axis: null,
            armed: false,
            height: dialog.offsetHeight || 1,
        };
        dialog.style.transition = "none";
    }

    move(event) {
        if (!this.drag) return;
        const dx = event.clientX - this.drag.startX;
        const dy = event.clientY - this.drag.startY;

        if (this.drag.axis === null) {
            const axis = lockAxis(dx, dy, AXIS_DEADZONE);
            if (axis === null) return;
            this.drag.axis = axis;
        }
        if (this.drag.axis !== "v") return;

        event.preventDefault();
        this.drag.dy = dy;

        // Downward tracks the finger 1:1; upward has nowhere to go, so it
        // resists rather than tearing the sheet off the bottom of the screen.
        const offset = dy >= 0 ? dy : rubberBand(dy, RESIST, RESIST_SCALE);
        this.drag.dialog.style.transform = "translateY(" + offset + "px)";

        const armed = dy > 0 && crossedThreshold(dy, DISMISS_MIN_PX, DISMISS_FRACTION, this.drag.height);
        if (armed !== this.drag.armed) {
            this.drag.armed = armed;
            if (armed) bump();   // one pulse as it crosses into "release to close"
        }
    }

    up() {
        if (!this.drag) return;
        const { modal, dialog, armed } = this.drag;
        this.drag = null;

        if (armed) {
            // Hand back to Bootstrap so the backdrop, focus restore and scroll
            // unlock all run exactly as they do for a close-button dismiss. The
            // sheet's own exit transition is in sheet.css.
            this.resetDialog(modal);
            const instance = window.bootstrap && window.bootstrap.Modal.getInstance(modal);
            if (instance) instance.hide();
            return;
        }

        dialog.style.transition = "transform var(--dur-base) " + motionToken("--ease-settle");
        dialog.style.transform = "";
        window.setTimeout(() => this.resetDialog(modal), 260);
    }

    resetDialog(modal) {
        const dialog = modal.querySelector(".modal-dialog");
        if (!dialog) return;
        dialog.style.transition = "";
        dialog.style.transform = "";
    }

    // MARK: turbo-frame modal lifecycle

    frameLoad(event) {
        if (event.target.id !== "modal") return;
        const modal = event.target.querySelector(".modal");
        if (!modal) return;
        // Bootstrap keeps one instance per element. If the frame reloaded while a
        // modal was already up, the existing instance is still the live one.
        if (window.bootstrap.Modal.getInstance(modal)) return;
        new window.bootstrap.Modal(modal, { backdrop: true, keyboard: true }).show();
    }

    submitEnd(event) {
        const modal = event.target.closest && event.target.closest(".modal");
        if (!modal || !event.detail.success) return;
        // The create panel presented as a sheet (forms_controller.js) closes itself
        // and announces its own "created" message; this generic "Updated" would be
        // a second, wrong toast.
        if (modal.matches('[data-controller~="forms"]')) return;
        const instance = window.bootstrap.Modal.getInstance(modal);
        if (instance) instance.hide();
        if (typeof window.showNotification === "function") {
            window.showNotification("Updated successfully", "success");
        }
    }
}
